import React from "react";
import PropTypes from "prop-types";
import { formatDuration } from "../../../../utils/format";

// TatCell — the honesty contract for the TAT dashboard.
//
// One interval cell is `{ state, minutes, note? }`. The whole point of this
// component is that the seven states are NOT interchangeable shades of grey:
// a stalled order and an in-flight order are both "not done yet", but one is
// fine and the other is silently rotting. If those two ever render the same,
// a multi-thousand-order backlog becomes invisible — which is precisely the
// failure this page exists to fix.
//
// Two pairs must stay unmistakably distinct, and they're distinguished
// STRUCTURALLY (different chrome), not just by hue — so they survive
// greyscale printing, colour-blind viewers, and a fast scan down 50 rows:
//
//   IN_PROGRESS  plain blue text with a small filled dot   →  "moving, ignore it"
//   STALE        orange block with a solid left accent bar →  "stuck, look at it"
//
//   NO_DATA      plain italic grey text, no chrome at all  →  "we measure this, this order lacks it"
//   NOT_TRACKED  bounded grey pill (rounded-full, border)  →  "we never measure this here"
//
// INVALID is the deliberate exception to "not interchangeable shades of grey": it
// renders identically to NO_DATA (see TAT_STATE_STYLES.INVALID for why) rather than
// getting its own protected pairing. It's rare enough, and disruptive enough at its old
// red-pill treatment, that "blends in, full reason on hover" won out over "always
// visually distinct."
//
// Every state also carries a `data-tat-state` attribute so tests (and later,
// visual QA) can assert on the rendered treatment rather than on copy.
//
// Keep this component tiny. Data shaping belongs in the page/mapper, not here.

/**
 * Per-state presentation. `className` is the full class list for the cell's
 * root element, in the LEGACY PillEngine palette only: the `elm` ramp, stock
 * Tailwind, and legacy's own hardcoded `#dc143c` / `#333`. No `.ops-v2` tokens
 * — the Ops Console has one visual language and it is the legacy one.
 *
 * Sizes are deliberately absent almost everywhere: `*` in src/index.scss sets
 * 13px app-wide, which is exactly legacy's body-cell size. Do not "restore" a
 * text-size class to these; and note `font-mono` emits nothing in this app
 * (no `mono` key in tailwind.config.js), which is why COMPLETE/SWEEP dropped
 * the monospace face rather than swapping it. Digit alignment is recovered by
 * `tabular-nums` on DataTable's <td>, not here.
 */
const TAT_STATE_STYLES = {
    // The number, and nothing else. A measured value needs no chrome.
    COMPLETE: "font-sans font-medium text-black",

    // Active: informational blue (#2563eb, legacy's own row-checkbox blue),
    // unbounded — reads as "in flight, fine". This string must contain
    // NEITHER "border" NOR "bg-": its bare-text-ness is the structural half
    // of the STALE/IN_PROGRESS distinction and is asserted as such.
    IN_PROGRESS: "inline-flex items-center gap-2 font-sans font-medium text-blue-600",

    // Stuck: orange warning block with a solid left accent bar (#f97316 rule
    // on a #ffedd5 fill, orange-700 text). Deliberately the heaviest
    // treatment on the row, and structurally unlike IN_PROGRESS (bounded box
    // + accent bar vs. bare text). Square, no radius.
    STALE:
        "inline-flex items-center gap-2 border-l-2 border-orange-500 bg-orange-100 py-0.5 pl-1.5 pr-2 font-sans font-semibold text-orange-700",

    // A real gap in data we DO collect: quiet, unbounded, italic — but
    // gray-500 (#6b7280, legacy's own placeholder grey), not gray-400.
    // Italic alone already costs legibility, so this state can't also be the
    // lightest grey on the row.
    NO_DATA: "font-sans italic text-gray-500",

    // A stage that does not exist for this order: a bounded pill at legacy's
    // 10px chip scale, on legacy's own inactive-chip fill and text. The
    // boundary is the message — this is a closed, deliberate "n/a", not a
    // hole where a number should have been.
    NOT_TRACKED:
        "inline-flex items-center rounded-full border border-gray-300 bg-gray-200 px-2 py-0.5 text-[10px] font-semibold uppercase font-sans text-[#333]",

    // Timestamps that contradict each other. Deliberately NOT visually distinct from
    // NO_DATA (both plain italic grey, no chrome) — a red "Invalid · Bad timestamps"
    // pill on every row was read as the table itself looking broken, not as a signal
    // about one order's data. The distinction this file's header insists on preserving
    // for IN_PROGRESS/STALE and NO_DATA/NOT_TRACKED is deliberately NOT extended here:
    // INVALID is rare (clock-skew/out-of-order writes), not a routine state ops scans
    // for row over row, so the cost of it blending into NO_DATA at a glance is small —
    // the full reason still surfaces on hover (see the `title` fallback below), it's
    // just no longer shouting from the page. `data-tat-state="INVALID"` still
    // distinguishes it for tests/tooling even though the rendered chrome doesn't.
    INVALID: "font-sans italic text-gray-500",

    // A number produced by a bulk administrative action, not a real event.
    // Dashed outline = "this figure is synthetic"; the ADMIN tag says so in
    // words too. font-medium matches COMPLETE's weight — this is still a
    // real duration, just administratively sourced; it shouldn't look
    // fainter than the number it's structurally distinguished from.
    SWEEP:
        "inline-flex items-center gap-2 rounded-md border border-dashed border-gray-400 bg-transparent px-2 py-0.5 font-sans font-medium text-black",
};

/**
 * Fallback text for NO_DATA/INVALID when they carry no `note`, and the
 * ALWAYS-shown chip text for NOT_TRACKED (whose own `note`, if any, only
 * ever reaches the `title` tooltip — see stateContent below).
 */
const DEFAULT_NOTES = {
    NO_DATA: "No data",
    NOT_TRACKED: "Not tracked",
    INVALID: "Bad timestamps",
};

/** Renders the inner content for one state. */
function stateContent(state, minutes, note) {
    switch (state) {
        case "COMPLETE":
            return formatDuration(minutes);
        case "IN_PROGRESS":
            return (
                <>
                    <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-blue-600" />
                    In progress · {formatDuration(minutes)}
                </>
            );
        case "STALE":
            return <>Stalled · {formatDuration(minutes)}</>;
        case "NOT_TRACKED":
            // Always the short fixed label, never `note` — real notes here
            // can be a full sentence ("Subscription refills skip the
            // quotation stage"), and cramming that into a 10px uppercase
            // pill is unreadable (flagged in whole-branch visual review).
            // The full `note` is still available via the `title` tooltip
            // the root <span> sets below, unconditionally, for every state.
            return DEFAULT_NOTES.NOT_TRACKED;
        case "INVALID":
            // Blank face, same as NO_DATA — the reason lives in the `title` tooltip
            // only (TatCell's title fallback below). See TAT_STATE_STYLES.INVALID.
            return "—";
        case "SWEEP":
            return (
                <>
                    <span className="text-[10px] font-semibold uppercase font-sans text-gray-500">
                        Admin
                    </span>
                    {formatDuration(minutes)}
                </>
            );
        case "NO_DATA":
        default:
            return note || DEFAULT_NOTES.NO_DATA;
    }
}

function TatCell({ value }) {
    // A missing cell is a missing measurement, not a crash. An interval key
    // present in meta.intervals but absent from a row falls through to
    // NO_DATA rather than rendering blank.
    const state = TAT_STATE_STYLES[value?.state] ? value.state : "NO_DATA";
    const { minutes = null, note = null } = value || {};

    return (
        <span
            data-tat-state={state}
            // Falls back to the state's own default explanation (DEFAULT_NOTES) when
            // there's no per-interval `note` — which is every real row today; the wire
            // format has no per-interval note field. Without this fallback, INVALID's
            // blank face (see TAT_STATE_STYLES.INVALID) would carry its reason nowhere
            // at all, not even on hover.
            title={note || DEFAULT_NOTES[state] || undefined}
            className={TAT_STATE_STYLES[state]}
        >
            {stateContent(state, minutes, note)}
        </span>
    );
}

TatCell.propTypes = {
    value: PropTypes.shape({
        state: PropTypes.oneOf(Object.keys(TAT_STATE_STYLES)),
        minutes: PropTypes.number,
        note: PropTypes.string,
    }),
};

TatCell.defaultProps = {
    value: null,
};

export { TAT_STATE_STYLES };
export default TatCell;
